import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';
import { Instagram, LifeBuoy, Mail, MessageCircle } from 'lucide-react';

import CONFIG from '../../config/config';
import { Modal } from '../Modal/Modal';

const contactButtonClass =
  'group flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 font-semibold text-gray-900 transition-colors hover:border-gray-900 hover:bg-gray-900 hover:text-white sm:min-w-[10rem]';

const contactLabelClass =
  'block text-xs font-normal text-gray-500 group-hover:text-white group-hover:opacity-80';

function HelpContactDialog() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const buttonLabel = t('help.button');
  const professionalPlanName = t('pricing.advancedStore.name');

  const openModal = () => {
    datadogRum.addAction('Help Contact - Opened');
    setIsOpen(true);
  };

  const closeModal = () => setIsOpen(false);

  const handlePlanClick = () => {
    datadogRum.addAction('Help Contact - Professional Plan Clicked');
    closeModal();
  };

  const handleWhatsApp = () => {
    datadogRum.addAction('Help Contact - WhatsApp Clicked');
    window.open(CONFIG.CONTACT_INFO.WHATSAPP, '_blank', 'noopener,noreferrer');
  };

  const handleEmail = () => {
    datadogRum.addAction('Help Contact - Email Clicked');
    window.location.href = `mailto:${CONFIG.CONTACT_INFO.EMAIL}`;
  };

  const handleInstagram = () => {
    datadogRum.addAction('Help Contact - Instagram Clicked');
    window.open(CONFIG.CONTACT_INFO.INSTAGRAM, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    if (isOpen) {
      datadogRum.addAction('Help Contact - Modal Viewed');
    }
  }, [isOpen]);

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        aria-label={buttonLabel}
        title={buttonLabel}
        data-dd-action-name="Help Contact Button"
        className="inline-flex items-center gap-1.5 rounded-full bg-gray-900 px-2.5 py-1.5 text-white shadow-lg transition hover:scale-105 hover:bg-gray-800 hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
      >
        <LifeBuoy className="h-4 w-4 shrink-0" aria-hidden />
        <span className="hidden text-sm font-medium md:inline">{buttonLabel}</span>
      </button>

      {isOpen && (
        <Modal modalTitle="help.modal.title" canClose onClose={closeModal}>
          <div className="space-y-6">
            <div className="space-y-3">
              <p className="text-gray-700 leading-relaxed">{t('help.modal.intro')}</p>
              <ul className="list-disc space-y-1 pl-5 text-sm text-gray-600">
                <li>{t('help.modal.example.technical')}</li>
                <li>{t('help.modal.example.setup')}</li>
                <li>{t('help.modal.example.module')}</li>
                <li>{t('help.modal.example.services')}</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">{t('help.modal.intro_closing')}</p>
            </div>
            <p className="text-gray-600 text-sm">{t('help.modal.language')}</p>

            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <button
                type="button"
                onClick={handleWhatsApp}
                className={contactButtonClass}
              >
                <MessageCircle className="h-5 w-5 shrink-0" aria-hidden />
                <span className="text-left">
                  <span className={contactLabelClass}>{t('help.modal.whatsapp')}</span>
                  <span>{CONFIG.CONTACT_INFO.PHONE}</span>
                </span>
              </button>

              <button
                type="button"
                onClick={handleEmail}
                className={contactButtonClass}
              >
                <Mail className="h-5 w-5 shrink-0" aria-hidden />
                <span className="text-left">
                  <span className={contactLabelClass}>{t('help.modal.email')}</span>
                  <span>{CONFIG.CONTACT_INFO.EMAIL}</span>
                </span>
              </button>

              <button
                type="button"
                onClick={handleInstagram}
                className={contactButtonClass}
              >
                <Instagram className="h-5 w-5 shrink-0" aria-hidden />
                <span className="text-left">
                  <span className={contactLabelClass}>{t('help.modal.instagram')}</span>
                  <span>@eateliers</span>
                </span>
              </button>
            </div>

            <div className="rounded-lg border-l-4 border-amber-600 bg-amber-50 p-4 text-sm text-amber-900">
              <Trans
                i18nKey="help.modal.professional_priority"
                values={{ plan: professionalPlanName }}
                components={{
                  planName: (
                    <Link
                      to="/subscription"
                      onClick={handlePlanClick}
                      className="font-semibold text-amber-700 underline-offset-2 hover:underline"
                    />
                  ),
                }}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

export default HelpContactDialog;
